import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import EvaluationNavigation from '../../components/EvaluationNavigation';
import theme from '../../theme/theme';
import { getMyEvaluationDetailsAsync } from '../../services/evaluationService';
import { useParams } from 'react-router-dom';
import MySpinner from '../../components/MySpinner';

const DetailsEvaluation = () => {
  const { empEvalId } = useParams();
  const [evaluationDetails, setEvaluationDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEvaluationDetails = async () => {
      setLoading(true);
      setIsFetched(false);
      setError(null);

      try {
        const response = await getMyEvaluationDetailsAsync(empEvalId);
        setEvaluationDetails(response);
      } catch (err) {
        setError(err.message || 'حدث خطأ أثناء جلب البيانات.');
      } finally {
        setLoading(false);
        setIsFetched(true);
      }
    };

    fetchEvaluationDetails();
  }, [empEvalId]);

  if (loading) return <Layout><MySpinner color={theme.colors.primary} /></Layout>;

  if (error) return (
    <Layout>
      <EvaluationNavigation />
      <div className="alert alert-danger text-center my-5" style={{ maxWidth: '600px', margin: '2rem auto' }}>
        <strong>خطأ:</strong> {error}
      </div>
    </Layout>
  );

  if (!isFetched || !evaluationDetails || Object.keys(evaluationDetails).length === 0) {
    return (
      <Layout>
        <EvaluationNavigation />
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>📋</div>
          <p style={styles.emptyText}>لم يتم العثور على تفاصيل التقييم</p>
        </div>
      </Layout>
    );
  }

  const ratingWords = {
    5: 'ممتاز',
    4: 'جيد جدا',
    3: 'جيد',
    2: 'مرضي',
    1: 'غير مرضي',
  };

  const ratingColors = {
    5: { bg: '#e8f5e9', color: '#2e7d32', border: '#4caf50' },
    4: { bg: '#e3f2fd', color: '#1565c0', border: '#2196f3' },
    3: { bg: '#fff3e0', color: '#e65100', border: '#ff9800' },
    2: { bg: '#fff8e1', color: '#f57c00', border: '#ffc107' },
    1: { bg: '#ffebee', color: '#c62828', border: '#f44336' },
  };

  const getRatingStars = (rating) => {
    return ''.repeat(rating);
  };

  return (
    <Layout>
      <EvaluationNavigation />

      <div style={styles.container}>
        {/* Header Card */}
        <div style={styles.headerCard}>
          <div style={styles.headerTop}>
            <h2 style={styles.headerTitle}>
              <span style={styles.headerIcon}>📄</span>
              تفاصيل التقييم
            </h2>
            <span style={{
              ...styles.statusBadge,
              ...(evaluationDetails.isSubmitted ? styles.submittedBadge : styles.draftBadge)
            }}>
              {evaluationDetails.isSubmitted ? '✓ معتمد' : '⏳ مسودة'}
            </span>
          </div>
        </div>

        {/* Info Card */}
        <div style={styles.infoCard}>
          <div style={styles.infoHeader}>
            <h3 style={styles.infoTitle}>معلومات التقييم</h3>
          </div>

          <div style={styles.infoBody}>
            <div style={styles.infoGrid}>
              <div style={styles.infoItem}>
                <span style={styles.infoIcon}>📅</span>
                <div style={styles.infoContent}>
                  <span style={styles.infoLabel}>السنة الأكاديمية</span>
                  <span style={styles.infoValue}>{evaluationDetails.academicYear}</span>
                </div>
              </div>

              {evaluationDetails.empName && (
                <div style={styles.infoItem}>
                  <span style={styles.infoIcon}>👤</span>
                  <div style={styles.infoContent}>
                    <span style={styles.infoLabel}>الاسم</span>
                    <span style={styles.infoValue}>{evaluationDetails.empName}</span>
                  </div>
                </div>
              )}

              <div style={styles.infoItem}>
                <span style={styles.infoIcon}>🏢</span>
                <div style={styles.infoContent}>
                  <span style={styles.infoLabel}>القسم</span>
                  <span style={styles.infoValue}>{evaluationDetails.deptName}</span>
                </div>
              </div>

              <div style={styles.infoItem}>
                <span style={styles.infoIcon}>💼</span>
                <div style={styles.infoContent}>
                  <span style={styles.infoLabel}>المنصب الوظيفي</span>
                  <span style={styles.infoValue}>{evaluationDetails.positionTitle}</span>
                </div>
              </div>
            </div>

            {/* Overall Percentage */}
            <div style={styles.percentageBox}>
              <div style={styles.percentageContent}>
                <span style={styles.percentageIcon}>📊</span>
                <span style={styles.percentageLabel}>النسبة المئوية الكلية</span>
              </div>
              <span style={styles.percentageValue}>
                {evaluationDetails.overallPercentage ? `${evaluationDetails.overallPercentage}%` : 'غير متوفرة'}
              </span>
            </div>
          </div>
        </div>

        {/* Evaluation Items */}
        <div style={styles.itemsSection}>
          <div style={styles.itemsHeader}>
            <h3 style={styles.itemsTitle}>
              <span style={styles.itemsTitleIcon}>📝</span>
              بنود التقييم
            </h3>
            <span style={styles.itemsCount}>
              {evaluationDetails.evaluationItems.length} بند
            </span>
          </div>

          <div style={styles.itemsGrid}>
            {evaluationDetails.evaluationItems.map((item, index) => {
              const ratingStyle = ratingColors[item.rating] || ratingColors[3];
              return (
                <div key={item.evaluationItemId} style={styles.evaluationItem}>
                  <div style={styles.itemHeader}>
                    <span style={styles.itemNumber}>#{index + 1}</span>
                    <h4 style={styles.itemName}>{item.itemName}</h4>
                  </div>

                  <div style={{
                    ...styles.ratingBox,
                    backgroundColor: ratingStyle.bg,
                    borderColor: ratingStyle.border
                  }}>
                    <div style={styles.ratingContent}>
                      <span style={styles.ratingStars}>{getRatingStars(item.rating)}</span>
                      <span style={{ ...styles.ratingText, color: ratingStyle.color }}>
                        {ratingWords[item.rating] || 'غير متوفر'}
                      </span>
                    </div>
                    <div style={{ ...styles.ratingScore, color: ratingStyle.color }}>
                      {item.rating}/5
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Summary Stats */}
        <div style={styles.summaryCard}>
          <h4 style={styles.summaryTitle}>ملخص التقييم</h4>
          <div style={styles.summaryGrid}>
            <div style={styles.summaryItem}>
              <span style={styles.summaryIcon}>📊</span>
              <div style={styles.summaryContent}>
                <span style={styles.summaryLabel}>إجمالي البنود</span>
                <span style={styles.summaryValue}>{evaluationDetails.evaluationItems.length}</span>
              </div>
            </div>
            <div style={styles.summaryItem}>
              <span style={styles.summaryIcon}></span>
              <div style={styles.summaryContent}>
                <span style={styles.summaryLabel}>متوسط التقييم</span>
                <span style={styles.summaryValue}>
                  {(evaluationDetails.evaluationItems.reduce((sum, item) => sum + item.rating, 0) / evaluationDetails.evaluationItems.length).toFixed(2)}
                </span>
              </div>
            </div>
            <div style={styles.summaryItem}>
              <span style={styles.summaryIcon}>✅</span>
              <div style={styles.summaryContent}>
                <span style={styles.summaryLabel}>الحالة</span>
                <span style={styles.summaryValue}>
                  {evaluationDetails.isSubmitted ? 'معتمد' : 'مسودة'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

const styles = {
  container: {
    maxWidth: '1000px',
    margin: '0 auto',
    padding: '2rem',
    direction: 'rtl'
  },
  headerCard: {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '2rem',
    marginBottom: '2rem',
    boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
    border: '1px solid #e0e0e0'
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '1rem'
  },
  headerTitle: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#1a1a1a',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    margin: 0
  },
  headerIcon: {
    fontSize: '2rem'
  },
  statusBadge: {
    padding: '0.6rem 1.25rem',
    borderRadius: '20px',
    fontSize: '0.95rem',
    fontWeight: 'bold'
  },
  submittedBadge: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    border: '2px solid #4caf50'
  },
  draftBadge: {
    backgroundColor: '#fff3e0',
    color: '#e65100',
    border: '2px solid #ff9800'
  },
  infoCard: {
    backgroundColor: 'white',
    borderRadius: '16px',
    overflow: 'hidden',
    marginBottom: '2rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    border: '1px solid #e0e0e0'
  },
  infoHeader: {
    backgroundColor: '#f8f9fa',
    padding: '1.25rem 1.5rem',
    borderBottom: '2px solid #e0e0e0'
  },
  infoTitle: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#1a1a1a',
    margin: 0
  },
  infoBody: {
    padding: '2rem'
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '1.25rem',
    marginBottom: '1.5rem'
  },
  infoItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    padding: '1rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '12px',
    border: '1px solid #e0e0e0'
  },
  infoIcon: {
    fontSize: '1.5rem',
    marginTop: '0.2rem'
  },
  infoContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    flex: 1
  },
  infoLabel: {
    fontSize: '0.85rem',
    color: '#666',
    fontWeight: '500'
  },
  infoValue: {
    fontSize: '1rem',
    color: '#1a1a1a',
    fontWeight: 'bold'
  },
  percentageBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem',
    backgroundColor: '#e3f2fd',
    borderRadius: '12px',
    border: '2px solid #2196f3'
  },
  percentageContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem'
  },
  percentageIcon: {
    fontSize: '1.75rem'
  },
  percentageLabel: {
    fontSize: '1.1rem',
    color: '#1565c0',
    fontWeight: '600'
  },
  percentageValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#0d47a1'
  },
  itemsSection: {
    marginBottom: '2rem'
  },
  itemsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
    gap: '1rem'
  },
  itemsTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#1a1a1a',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    margin: 0
  },
  itemsTitleIcon: {
    fontSize: '1.5rem'
  },
  itemsCount: {
    padding: '0.5rem 1rem',
    borderRadius: '20px',
    backgroundColor: '#f0f0f0',
    color: '#666',
    fontSize: '0.9rem',
    fontWeight: 'bold'
  },
  itemsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  evaluationItem: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '1.5rem',
    border: '1px solid #e0e0e0',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)'
  },
  itemHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '1rem'
  },
  itemNumber: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: '#f0f0f0',
    color: '#666',
    fontSize: '0.9rem',
    fontWeight: 'bold'
  },
  itemName: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#1a1a1a',
    margin: 0,
    flex: 1
  },
  ratingBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem 1.25rem',
    borderRadius: '12px',
    border: '2px solid'
  },
  ratingContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem'
  },
  ratingStars: {
    fontSize: '1.25rem'
  },
  ratingText: {
    fontSize: '1.1rem',
    fontWeight: 'bold'
  },
  ratingScore: {
    fontSize: '1.5rem',
    fontWeight: 'bold'
  },
  summaryCard: {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '2rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    border: '1px solid #e0e0e0'
  },
  summaryTitle: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#1a1a1a',
    marginBottom: '1.5rem',
    textAlign: 'center'
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1.25rem'
  },
  summaryItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    padding: '1.25rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '12px',
    border: '1px solid #e0e0e0'
  },
  summaryIcon: {
    fontSize: '2rem'
  },
  summaryContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem'
  },
  summaryLabel: {
    fontSize: '0.85rem',
    color: '#666',
    fontWeight: '500'
  },
  summaryValue: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#1a1a1a'
  },
  emptyState: {
    textAlign: 'center',
    padding: '4rem 2rem',
    backgroundColor: '#fafafa',
    borderRadius: '16px',
    border: '2px dashed #e0e0e0',
    maxWidth: '600px',
    margin: '2rem auto'
  },
  emptyIcon: {
    width: '80px',
    height: '80px',
    margin: '0 auto 1.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor: '#fff3e0',
    color: '#ff9800',
    fontSize: '2.5rem'
  },
  emptyText: {
    fontSize: '1.1rem',
    color: '#666',
    margin: 0
  }
};

export default DetailsEvaluation;